import { BackgroundCategoryStatus, LawsuitRole, LawsuitRoleConfidence, SeriesPoint, ShareholderSource, SnapshotFileStatus } from '@data/interfaces/verification-snapshot.interface'

/**
 * Diccionarios de la foto de Syntage (F-158). Una sola fuente de verdad para las etiquetas en español
 * que pintan Análisis, Buró y deuda, y Legal y corporativo.
 *
 * Las llaves son las de Syntage / de la API tal cual llegan; las etiquetas son lo que lee el usuario.
 * Se midieron sobre las tres muestras anonimizadas de `tabs/__fixtures__/`.
 *
 * ! Los códigos que deduce el backend (papel en juicio, confianza, fuente del accionista, estado de
 * ! categoría) llegan EN INGLÉS; manda lo que devuelve la API, no una traducción previa
 * ! (`demandado`, `por_confirmar`…).
 */

/** Las 13 llaves exactas de `/insights/risks` (la documentación de Syntage listaba 12). */
export const RISK_KEYS = [
  'taxCompliance',
  'blacklistStatus',
  'blacklistedCounterparties',
  'intercompanyTransactions',
  'customerConcentration',
  'supplierConcentration',
  'foreignExchangeRisk',
  'cashTransactionRisk',
  'accountingInsolvency',
  'canceledIssuedInvoices',
  'canceledReceivedInvoices',
  'infonavitOverdueCredits',
  'moratoryInterest',
] as const

export type RiskKey = (typeof RISK_KEYS)[number]

export const RISK_LABELS: Record<RiskKey, string> = {
  taxCompliance: 'Cumplimiento fiscal (32-D)',
  blacklistStatus: 'Estatus propio en lista 69-B',
  blacklistedCounterparties: 'Contrapartes en lista 69-B',
  intercompanyTransactions: 'Operaciones entre partes relacionadas',
  customerConcentration: 'Concentración de clientes',
  supplierConcentration: 'Concentración de proveedores',
  foreignExchangeRisk: 'Riesgo cambiario',
  cashTransactionRisk: 'Operaciones en efectivo',
  accountingInsolvency: 'Insolvencia contable',
  canceledIssuedInvoices: 'Facturas emitidas canceladas',
  canceledReceivedInvoices: 'Facturas recibidas canceladas',
  infonavitOverdueCredits: 'Créditos Infonavit vencidos',
  moratoryInterest: 'Intereses moratorios',
}

/**
 * Resultado de la opinión de cumplimiento 32-D tal como lo manda Syntage. Sin diccionario, «Cumplimiento
 * fiscal (32-D)» se leía «positive»/«negative» en los riesgos.
 */
export const TAX_COMPLIANCE_LABELS: Record<string, string> = {
  positive: 'Positiva',
  negative: 'Negativa',
  no_obligations: 'Sin obligaciones',
  suspended: 'Suspendido',
}

/** Categorías de la Verificación de Antecedentes (`BackgroundCheckCategory` de Syntage). */
export const BACKGROUND_CATEGORY_LABELS: Record<string, string> = {
  personal_identity: 'Identidad personal',
  criminal_record: 'Antecedentes penales',
  legal_background: 'Antecedentes judiciales',
  business_background: 'Antecedentes empresariales',
  international_background: 'Listas internacionales y sanciones',
  professional_background: 'Antecedentes profesionales',
  taxes_and_finances: 'Impuestos y finanzas',
  politically_exposed_person: 'Persona políticamente expuesta (PEP)',
  alert_in_media: 'Alertas en medios',
  credit_history: 'Historial crediticio',
  document_validation: 'Validación de documentos',
  affiliations_and_insurances: 'Afiliaciones y seguros',
  behavior: 'Comportamiento',
  driving_licenses: 'Licencias de conducir',
  traffic_fines: 'Multas de tránsito',
  vehicle_information: 'Información vehicular',
  vehicle_permits: 'Permisos vehiculares',
  unknown: 'Sin categoría',
}

/** Estatus en el listado 69-B del SAT (`blacklistStatus` de contribuyentes y contrapartes). */
export const BLACKLIST_69B_STATUS_LABELS: Record<string, string> = {
  presumed: 'Presunto',
  definitive: 'Definitivo',
  dismissed: 'Desvirtuado',
  favorable: 'Sentencia favorable',
}

/** Diccionario del valor de los riesgos que llegan como código; los demás son números o sí/no. */
export const RISK_VALUE_LABELS: Partial<Record<RiskKey, Record<string, string>>> = {
  taxCompliance: TAX_COMPLIANCE_LABELS,
  blacklistStatus: BLACKLIST_69B_STATUS_LABELS,
}

/**
 * Unidad del `value` de cada riesgo. Syntage lo manda como número suelto, y sin unidad «Riesgo cambiario 0.25»
 * o «Concentración 1,508» no se entendían (F-158). Medido sobre las tres muestras anonimizadas:
 * - `proportion`: fracción de 0 a 1 (0.25 = 25 %).
 * - `hhi`: índice Herfindahl-Hirschman de concentración (380, 1508).
 * - `ratio`: razón contable (1.13).
 * - `pesos`: monto en pesos con centavos (87908.22).
 * - `count`: conteo.
 * - `code`: código con diccionario en `RISK_VALUE_LABELS`.
 * - `listing`: estatus 69-B; `null` quiere decir que el contribuyente no está en el listado.
 */
export type RiskValueFormat = 'proportion' | 'hhi' | 'ratio' | 'pesos' | 'count' | 'code' | 'listing'

export const RISK_VALUE_FORMAT: Record<RiskKey, RiskValueFormat> = {
  taxCompliance: 'code',
  blacklistStatus: 'listing',
  blacklistedCounterparties: 'count',
  intercompanyTransactions: 'count',
  customerConcentration: 'hhi',
  supplierConcentration: 'hhi',
  foreignExchangeRisk: 'proportion',
  cashTransactionRisk: 'proportion',
  accountingInsolvency: 'ratio',
  canceledIssuedInvoices: 'proportion',
  canceledReceivedInvoices: 'proportion',
  infonavitOverdueCredits: 'count',
  moratoryInterest: 'pesos',
}

const PESOS = new Intl.NumberFormat('es-MX', { style: 'currency', currency: 'MXN', minimumFractionDigits: 0, maximumFractionDigits: 0 })

/** Pesos sin centavos (`$86,203,237`), como el resto de los montos de la pantalla. */
export function formatPesos(value: number): string {
  return PESOS.format(value)
}

/**
 * Pesos para una cifra de franja: de un millón para arriba en millones con un decimal (`$86.2M`), abajo completos.
 * Quien la pinta pone el monto completo en `title`. Sin monto, «—».
 */
export function formatPesosCompact(value: number | null | undefined): string {
  if (value === null || value === undefined || !Number.isFinite(value)) return '—'
  if (Math.abs(value) >= 1_000_000) return `${value < 0 ? '-' : ''}$${(Math.abs(value) / 1_000_000).toFixed(1)}M`
  return formatPesos(value)
}

const SHORT_DATE: Intl.DateTimeFormatOptions = { day: 'numeric', month: 'short', year: 'numeric' }

/**
 * Fecha corta de las tablas y franjas de Algoritmo (`17 ago 2026`). Sin fecha o ilegible, «—».
 *
 * - `AAAA-MM-DD` es un día del calendario, sin zona, igual que en `MxDatePipe`.
 * - `AAAA-MM-DD HH:mm[:ss]` sin zona (así manda Buró su `createdAt`) ya es hora de la Ciudad de México: vale su día tal
 *   cual. Leerlo con la zona de la máquina podía moverlo un día cerca de la medianoche.
 * - Con zona (`…T05:30:00Z`, `…+00:00`), el día que era en la Ciudad de México.
 */
export function formatShortDate(value: string | null | undefined): string {
  if (!value) return '—'
  const day = /^(\d{4})-(\d{2})-(\d{2})(?:$| \d{2}:\d{2}(?::\d{2})?$)/.exec(value)
  if (day) return new Date(Number(day[1]), Number(day[2]) - 1, Number(day[3])).toLocaleDateString('es-MX', SHORT_DATE).replace(/\./g, '')
  const date = new Date(value)
  if (isNaN(date.getTime())) return '—'
  return date.toLocaleDateString('es-MX', { ...SHORT_DATE, timeZone: 'America/Mexico_City' }).replace(/\./g, '')
}

/** Tono de las pastillas de Algoritmo (`.alg-pill.is-*`) para cada severidad que ya usan las etiquetas. */
export type PillTone = 'ok' | 'warn' | 'info' | 'bad' | 'neutral'

export const SEVERITY_TONE: Record<'success' | 'warning' | 'info' | 'danger' | 'secondary', PillTone> = {
  success: 'ok',
  warning: 'warn',
  info: 'info',
  danger: 'bad',
  secondary: 'neutral',
}

function finiteNumber(value: unknown): number | null {
  if (value === null || value === undefined || value === '' || typeof value === 'boolean') return null
  const n = Number(value)
  return Number.isFinite(n) ? n : null
}

/**
 * El valor de un riesgo en español y con su unidad (`RISK_VALUE_FORMAT`). Una llave que Syntage agregue
 * después no tiene unidad conocida: se pinta el número tal cual, sin inventarle una.
 */
export function formatRiskValue(key: string, value: unknown): string {
  const format = RISK_VALUE_FORMAT[key as RiskKey]
  if (value === null || value === undefined) return format === 'listing' ? 'Ninguno' : '—'
  if (typeof value === 'object') return '—'
  if (typeof value === 'boolean') return value ? 'Sí' : 'No'
  const n = finiteNumber(value)
  if (n === null || format === 'code' || format === 'listing') return labelOf(RISK_VALUE_LABELS[key as RiskKey] ?? {}, String(value))
  switch (format) {
    case 'proportion':
      return `${(n * 100).toLocaleString('es-MX', { maximumFractionDigits: 1 })} %`
    case 'hhi':
      return `HHI ${Math.round(n).toLocaleString('es-MX')}`
    case 'ratio':
      return `Razón ${n.toLocaleString('es-MX', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
    case 'pesos':
      return formatPesos(n)
    case 'count':
      return Math.round(n).toLocaleString('es-MX')
    default:
      return n.toLocaleString('es-MX', { maximumFractionDigits: 2 })
  }
}

/**
 * MOP (forma de pago) del catálogo público de Buró de Crédito.
 *
 * ! Verificado solo contra el catálogo de personas físicas. La cadena `historicoPagos` del Buró
 * ! Empresas (PM) no se ha contrastado con el PDF del reporte: quien pinte el historial de cuentas lo
 * ! valida con los fixtures antes de confiar.
 */
export const BUREAU_MOP_LABELS: Record<string, string> = {
  '00': 'Cuenta muy reciente para calificarse',
  '01': 'Al corriente',
  '02': 'Atraso de 1 a 29 días',
  '03': 'Atraso de 30 a 59 días',
  '04': 'Atraso de 60 a 89 días',
  '05': 'Atraso de 90 a 119 días',
  '06': 'Atraso de 120 a 149 días',
  '07': 'Atraso de 150 días a 12 meses',
  '96': 'Atraso de 12 meses o más',
  '97': 'Deuda parcial o total sin recuperar',
  '99': 'Fraude cometido por el cliente',
  UR: 'Sin información',
}

/** La forma corta del MOP para la pastilla de la tabla (maqueta F-158); la larga va en su `title` y para el lector. */
export const BUREAU_MOP_SHORT_LABELS: Record<string, string> = {
  '00': 'Muy reciente',
  '01': 'Al corriente',
  '02': 'Atraso 1–29 d',
  '03': 'Atraso 30–59 d',
  '04': 'Atraso 60–89 d',
  '05': 'Atraso 90–119 d',
  '06': 'Atraso 120–149 d',
  '07': 'Atraso 150 d–12 m',
  '96': 'Atraso 12 m o más',
  '97': 'Deuda sin recuperar',
  '99': 'Fraude',
  UR: 'Sin información',
}

/**
 * El MOP de una cuenta, a partir de su `historicoPagos`.
 *
 * El Buró de personas morales manda un dígito por mes (`'1'`) y el catálogo usa dos (`'01'`), así que
 * se rellena con cero. Se toma el **peor** mes de la cadena y no el primero ni el último: no está
 * verificado contra el PDF en qué extremo queda el mes más reciente (ver el aviso de `BUREAU_MOP_LABELS`),
 * y el peor atraso no depende del orden.
 */
export function worstMop(history: string | null | undefined): string | null {
  const codes = (history ?? '').split('').filter((c) => /\d/.test(c) && c !== '0')
  if (!codes.length) return null
  return codes.sort().pop()!.padStart(2, '0')
}

/** Una clave de la calificación de cartera de Buró con cuántas veces aparece. */
export interface PortfolioRatingItem {
  code: string
  count: number
}

/**
 * Calificación de cartera de Buró (`portfolioRating` y `calificacioncartera` por mes): pares de 2 dígitos de
 * cantidad y 2 caracteres de clave. «01EX01C101A202A1» = EX ×1, C1 ×1, A2 ×1 y A1 ×2. Se agrupa por clave en orden
 * alfabético. `null` si el texto no sigue ese formato: entonces se pinta tal cual llegó.
 *
 * ! No hay catálogo verificable de lo que significa cada clave (en las muestras: A1, A2, B1, B3, C1, EX, NC). El
 * ! panel muestra la clave y su cantidad, y no les pone significado ni color de riesgo (F-158).
 */
export function decodePortfolioRating(raw: string | null | undefined): PortfolioRatingItem[] | null {
  const text = (raw ?? '').trim()
  if (!text || text.length % 4 !== 0) return null
  const counts = new Map<string, number>()
  for (let i = 0; i < text.length; i += 4) {
    const pair = /^(\d{2})([A-Z][A-Z0-9])$/.exec(text.slice(i, i + 4))
    if (!pair || Number(pair[1]) === 0) return null
    counts.set(pair[2], (counts.get(pair[2]) ?? 0) + Number(pair[1]))
  }
  return [...counts.entries()].sort(([a], [b]) => a.localeCompare(b)).map(([code, count]) => ({ code, count }))
}

/**
 * La calificación de cartera en una línea («A1 ×2 · A2 ×1 · EX ×1»), para la tabla por mes, donde una pastilla por
 * clave alargaba cada renglón. Si el texto no se pudo decodificar, va tal cual llegó; vacío, «—».
 */
export function portfolioRatingText(raw: string | null | undefined): string {
  const items = decodePortfolioRating(raw)
  if (!items) return raw?.trim() || '—'
  //* Espacio duro entre clave y cantidad: la línea solo se parte entre pares, nunca «EX» de su «×1».
  return items.map((item) => `${item.code}\u00a0×${item.count}`).join(' · ')
}

/**
 * Escala del Score PyME de Buró de Crédito: de 100 a 400, la que Buró publica. Se fija aquí porque el reporte trae el
 * score pero no la escala, y sin ella no se puede pintar la barra ni el «/ 400» (F-158). Vive con nombre para no
 * regarla como número suelto.
 */
export const BUREAU_PYME_SCORE_RANGE = { min: 100, max: 400 } as const

/** Dónde cae el score dentro de su escala, de 0 a 100 (recortado a los extremos); `null` sin score. */
export function scorePosition(score: number | null | undefined, range: { min: number; max: number }): number | null {
  if (score === null || score === undefined || !Number.isFinite(score)) return null
  const position = ((score - range.min) / (range.max - range.min)) * 100
  return Math.min(100, Math.max(0, position))
}

/**
 * Primer y último mes de los `count` puntos más recientes de una serie mensual (`d` = `AAAA-MM-DD`). Es el rango del
 * «Promedio 3 meses» de intereses moratorios, que la API calcula con los 3 últimos puntos de `monthly[]`; el
 * `periodFrom`/`periodTo` del resumen es el de 12 meses y ahí mentía (F-158). `null` si la serie no alcanza.
 */
export function lastMonthsRange(points: SeriesPoint[] | null | undefined, count: number): { from: string; to: string } | null {
  const months = [...(points ?? [])].map((point) => point.d).sort()
  if (months.length < count) return null
  const last = months.slice(-count)
  return { from: last[0], to: last[last.length - 1] }
}

/**
 * Un cuadrito por mes del `historicoPagos`, en el orden en que lo manda Buró: `1` al corriente, `2` atraso de 1 a 29
 * días, de `3` a `9` atraso de 30 días o más; cualquier otro carácter (sin dato, `0`, `U`…) va vacío.
 */
export function historyTones(history: string | null | undefined): Array<'ok' | 'warn' | 'bad' | 'none'> {
  return (history ?? '').split('').map((c) => (c === '1' ? 'ok' : c === '2' ? 'warn' : /[3-9]/.test(c) ? 'bad' : 'none'))
}

/** Severidad del chip de MOP: al corriente en verde, 1-29 días en ámbar y de ahí para arriba en rojo. */
export function mopSeverity(mop: string | null): 'success' | 'warning' | 'danger' | 'secondary' {
  if (!mop) return 'secondary'
  if (mop === '01') return 'success'
  if (mop === '02') return 'warning'
  return 'danger'
}

/** Papel del cliente en un expediente. Lo deduce el backend (`lawsuit-role.ts`); «Actuación» no decide. */
export const LAWSUIT_ROLE_LABELS: Record<LawsuitRole, { label: string; severity: 'danger' | 'info' | 'warning' | 'secondary'; icon: string }> = {
  defendant: { label: 'Demandado', severity: 'danger', icon: 'fa-solid fa-triangle-exclamation' },
  plaintiff: { label: 'Demandante', severity: 'info', icon: 'fa-solid fa-gavel' },
  both: { label: 'Ambos', severity: 'warning', icon: 'fa-solid fa-right-left' },
  unconfirmed: { label: 'Por confirmar', severity: 'secondary', icon: 'fa-solid fa-circle-question' },
}

/** Con qué se reconoció al cliente en el expediente. Una coincidencia débil siempre queda «Por confirmar». */
export const LAWSUIT_CONFIDENCE_LABELS: Record<LawsuitRoleConfidence, string> = {
  rfc: 'Coincidencia por RFC',
  name: 'Coincidencia por nombre',
  partial: 'Coincidencia parcial',
  none: 'Sin coincidencia',
}

/** De dónde salió cada accionista: el backend prioriza RPC sobre Buró y Buró sobre el insight. */
export const SHAREHOLDER_SOURCE_LABELS: Record<ShareholderSource, string> = {
  rpc: 'RPC',
  bureau: 'Buró',
  insight: 'Syntage',
}

/**
 * Estado de una categoría de antecedentes.
 *
 * ! `not_requested` es «No consultado» y NUNCA «Sin antecedentes»: Syntage ignoró o saltó todas sus
 * ! bases. `not_found` solo llega cuando se consultó al menos una.
 */
export const BACKGROUND_STATUS_LABELS: Record<BackgroundCategoryStatus, string> = {
  found: 'Con hallazgos',
  not_found: 'Sin hallazgos',
  not_requested: 'No consultado',
  error: 'Error al consultar',
}

/**
 * Cómo se pinta un problema de la fuente, igual en Análisis, Legal y `app-snapshot-state`: lo pendiente en
 * ámbar con reloj de arena y el error de la fuente en rojo con el enchufe desconectado. Una sola definición
 * para que el mismo estado no cambie de color según la pestaña.
 */
export const SOURCE_ISSUE_STYLE = {
  pending: { severity: 'warning', icon: 'fa-solid fa-hourglass-half' },
  error: { severity: 'danger', icon: 'fa-solid fa-plug-circle-xmark' },
} as const

/** Chip del estado de una categoría de antecedentes: el texto y el ícono siempre acompañan al color. */
export const BACKGROUND_STATUS_STYLE: Record<BackgroundCategoryStatus, { severity: 'danger' | 'success' | 'secondary' | 'warning'; icon: string }> = {
  found: { severity: 'danger', icon: 'fa-solid fa-triangle-exclamation' },
  not_found: { severity: 'success', icon: 'fa-solid fa-check' },
  not_requested: { severity: 'secondary', icon: 'fa-solid fa-minus' },
  error: SOURCE_ISSUE_STYLE.error,
}

/**
 * Syntage califica cada categoría de antecedentes y el total de 0 a 1 (`score`) y en su tablero lo presenta de
 * 0 a 10, con 10 sin riesgo: el panel lo pinta igual, ×10.
 */
export const BACKGROUND_SCORE_SCALE = 10

/**
 * Calificación de una categoría sin riesgo.
 *
 * ! El rojo sale de la calificación de Syntage y no del conteo de registros (F-158). En las muestras, toda
 * ! categoría sin riesgo trae 1 aunque tenga registros: en ent-a, judiciales con 3 juicios donde el cliente es el
 * ! demandante, y empresariales e impuestos con certificados del SAT. Syntage solo descuenta cuando el registro
 * ! pesa en contra: en ent-b, judiciales baja a 0.6 con un juicio donde el cliente es el demandado.
 */
export const BACKGROUND_NO_RISK_SCORE = 1

/** `0.88` → «8.8 / 10» y `1` → «10 / 10»; sin calificación, «—». */
export function formatOutOfTen(score: number | null | undefined): string {
  if (score === null || score === undefined || !Number.isFinite(score)) return '—'
  return `${(score * BACKGROUND_SCORE_SCALE).toLocaleString('es-MX', { maximumFractionDigits: 1 })} / ${BACKGROUND_SCORE_SCALE}`
}

export interface BackgroundVerdict {
  label: string
  severity: 'danger' | 'success' | 'secondary' | 'warning' | 'info'
  icon: string
}

/**
 * El chip de una categoría de antecedentes. Con la calificación por debajo de `BACKGROUND_NO_RISK_SCORE`, rojo.
 * Con registros y sin descuento, «Sin riesgo»; en judiciales donde el cliente solo aparece como demandante
 * (`onlyAsPlaintiff`), «Registros (como demandante)». Sin calificación (`null`) no hay con qué descartar el
 * riesgo, así que los registros se siguen marcando como hallazgo.
 */
export function backgroundVerdict(category: { status: string; score: number | null }, onlyAsPlaintiff = false): BackgroundVerdict {
  const { status, score } = category
  if (status !== 'found' && status !== 'not_found') {
    const style = BACKGROUND_STATUS_STYLE[status as BackgroundCategoryStatus] ?? BACKGROUND_STATUS_STYLE.not_requested
    return { label: labelOf(BACKGROUND_STATUS_LABELS, status), ...style }
  }
  const hasScore = typeof score === 'number' && Number.isFinite(score)
  if (hasScore && score < BACKGROUND_NO_RISK_SCORE) return { label: BACKGROUND_STATUS_LABELS.found, ...BACKGROUND_STATUS_STYLE.found }
  if (status === 'not_found') return { label: BACKGROUND_STATUS_LABELS.not_found, ...BACKGROUND_STATUS_STYLE.not_found }
  if (onlyAsPlaintiff) return { label: 'Registros (como demandante)', severity: 'info', icon: LAWSUIT_ROLE_LABELS.plaintiff.icon }
  if (!hasScore) return { label: BACKGROUND_STATUS_LABELS.found, ...BACKGROUND_STATUS_STYLE.found }
  return { label: 'Sin riesgo', severity: 'success', icon: 'fa-solid fa-check' }
}

/**
 * Campos de una coincidencia de BIL con el nombre que les da el proveedor en su XML (`@CLAVE`, `JUICIO`, `PERIODO`,
 * `GRADO` y los de `INFORMACION`). Son etiquetas, no significados: BIL no documenta la escala del grado ni el
 * catálogo de claves, y el panel no los inventa (F-158).
 */
export const BIL_HIT_FIELD_LABELS: Record<'code' | 'lawsuit' | 'period' | 'grade', string> = {
  code: 'Clave',
  lawsuit: 'Juicio',
  period: 'Periodo',
  grade: 'Grado',
}

/** Campos de `INFORMACION` de una coincidencia DETALLADO, medidos en la muestra ent-a (Compranet). */
export const BIL_DETAIL_LABELS: Record<string, string> = {
  DEPENDENCIA: 'Dependencia',
  TITULO_CONTRATO: 'Título del contrato',
  IMPORTE: 'Importe',
  NUMERO_PROCEDIMIENTO: 'Número de procedimiento',
  GOBIERNO: 'Gobierno',
  PROVEEDOR: 'Proveedor',
  LINK: 'Liga',
}

/** Una coincidencia de BIL lista para leerse: fuente, tipo de reporte y sus campos con etiqueta. */
export interface BilHitView {
  source: string
  type: string
  fields: Array<{ label: string; value: string }>
}

const bilText = (value: unknown): string | null => {
  if (value === null || value === undefined || value === '') return null
  return typeof value === 'object' ? JSON.stringify(value) : String(value)
}

/** Los campos de una coincidencia en vez de códigos sueltos; un campo de `INFORMACION` sin etiqueta conocida se humaniza. */
export function bilHitView(hit: { type?: string; source?: string; code?: string; grade?: string; lawsuit?: string; period?: string; detail?: Record<string, unknown> }): BilHitView {
  const fields: BilHitView['fields'] = []
  for (const key of ['code', 'lawsuit', 'period', 'grade'] as const) {
    const value = bilText(hit[key])
    if (value !== null) fields.push({ label: BIL_HIT_FIELD_LABELS[key], value })
  }
  for (const [key, raw] of Object.entries(hit.detail ?? {})) {
    const value = bilText(raw)
    if (value !== null) fields.push({ label: BIL_DETAIL_LABELS[key] ?? humanizeCode(key.toLowerCase()), value })
  }
  return { source: hit.source ?? '—', type: hit.type === 'DETALLADO' ? 'Reporte detallado' : 'Reporte básico', fields }
}

/** Ventana del detalle de contrapartes 69-B; el indicador de riesgo cuenta solo los últimos 12 meses. */
export const COUNTERPARTY_WINDOW_LABELS: Record<string, string> = {
  historical: 'Histórico',
}

/** `prenda_sin_transmision_de_posesion` → «Prenda sin transmision de posesion». Para catálogos sin diccionario (RUG). */
export function humanizeCode(code: string | null | undefined): string {
  if (!code) return '—'
  const text = code.replace(/_/g, ' ').trim()
  return text.charAt(0).toUpperCase() + text.slice(1)
}

/**
 * Lo que dice el panel junto al botón PDF según el estado de copia del archivo; `null` = descargable, sin nota.
 * Solo `error` promete reintento: `unavailable` es definitivo y ahí no se ofrece el botón.
 */
export const FILE_STATUS_NOTES: Record<SnapshotFileStatus, string | null> = {
  copied: null,
  pending: 'El PDF todavía se está copiando.',
  error: 'No se pudo copiar el PDF; el sistema lo reintenta solo en las próximas horas.',
  unavailable: 'El PDF ya no está disponible en Syntage.',
}

/** Busca la etiqueta y, si la llave es nueva, devuelve el valor crudo en vez de esconderlo. */
export function labelOf(dictionary: Record<string, string>, key: string | null | undefined): string {
  if (!key) return '—'
  return dictionary[key] ?? key
}

/**
 * Motivos de una sección `error` que el backend reintenta solo, hasta `SNAPSHOT_SECTION_MAX_RETRIES` vueltas
 * del cron (~2 h, no toda la ventana de 48 h): red, tiempo,
 * límite de tasa, 5xx de Syntage, o no se pudo buscar la entidad del cliente (`SYNTAGE_UNAVAILABLE`).
 * Los definitivos (`SHAPE`, `HTTP_400`, `HTTP_404`, `EXTRACTION_FAILED`) no: repetir no cambia la respuesta.
 *
 * ! Espejo de `TRANSIENT_SECTION_REASON` en `api-rendafin` (`snapshot/utils/snapshot-record.ts`): si allá
 * ! cambia la lista, cambia aquí.
 */
const TRANSIENT_SOURCE_ERROR = /^(TIMEOUT|NETWORK|HTTP_429|HTTP_5\d\d|SYNTAGE_UNAVAILABLE)$/

/** `true` si el error de la fuente es pasajero y el sistema lo vuelve a intentar sin que nadie haga nada. */
export function isTransientSourceError(reason: string | null | undefined): boolean {
  return !!reason && TRANSIENT_SOURCE_ERROR.test(reason)
}
